import {
    Badge,
    BlockStack,
    Box,
    Card,
    DataTable,
    InlineStack,
    Typography,
    type DataTableColumn,
} from '@pv-frontend/ui-components-web';
import type { CrawlRunRow } from './crawls-runs-list';
import {
    statusBadge,
    dispositionBadge,
    coverageBadge,
    partialCoverageWarning,
    triggerLabel,
    scopeLabel,
    formatTimestamp,
} from './crawl-format';

/** One site's outcome within a run, as the drill-in renders it. */
export interface CrawlSiteRow {
    siteId: string;
    siteName: string;
    disposition: string;
    coverage: string;
    /** Why coverage fell short — the page that was skipped and its status, or the cap that cut the walk. */
    coverageNote: string | null;
    listingsAdded: number;
    listingsUpdated: number;
    listingsUnchanged: number;
    urlsInSitemap: number;
    rejectsQueued: number;
    listingsMissed: number;
    listingsDelisted: number;
    error: string | null;
    startedUtc: string;
    completedUtc: string;
}

const num = (value: number) => (
    <Typography variant="body-sm" tone="text-secondary">
        {value}
    </Typography>
);

const columns: ReadonlyArray<DataTableColumn<CrawlSiteRow>> = [
    {
        key: 'site',
        header: 'Site',
        render: (site) => (
            <BlockStack gap="050">
                <Typography variant="body-sm" fontWeight="semibold">
                    {site.siteName}
                </Typography>
                {site.error ? (
                    <Typography variant="body-sm" tone="text-critical" numberOfLines={2}>
                        {site.error}
                    </Typography>
                ) : null}
                {/* The cause sits right under the site, so a Partial badge doesn't send anyone to the logs. */}
                {coverageBadge(site.coverage, site.disposition) && site.coverageNote ? (
                    <Typography variant="body-sm" tone="text-warning" testID={`coverage-note-${site.siteId}`}>
                        {site.coverageNote}
                    </Typography>
                ) : null}
            </BlockStack>
        ),
    },
    {
        key: 'disposition',
        header: 'Disposition',
        render: (site) => {
            const look = dispositionBadge(site.disposition);
            const coverage = coverageBadge(site.coverage, site.disposition);
            return (
                <InlineStack gap="150" blockAlign="center" wrap>
                    <Badge tone={look.tone} dot>
                        {look.label}
                    </Badge>
                    {coverage ? (
                        <Badge tone={coverage.tone} testID={`coverage-${site.siteId}`}>
                            {coverage.label}
                        </Badge>
                    ) : null}
                </InlineStack>
            );
        },
    },
    { key: 'added', header: 'Added', align: 'end', render: (site) => num(site.listingsAdded) },
    { key: 'updated', header: 'Updated', align: 'end', render: (site) => num(site.listingsUpdated) },
    { key: 'unchanged', header: 'Unchanged', align: 'end', render: (site) => num(site.listingsUnchanged) },
    { key: 'missed', header: 'Missed', align: 'end', render: (site) => num(site.listingsMissed) },
    { key: 'delisted', header: 'Delisted', align: 'end', render: (site) => num(site.listingsDelisted) },
    { key: 'rejects', header: 'Rejects', align: 'end', render: (site) => num(site.rejectsQueued) },
];

/** A labelled figure in the run header summary. */
function Metric({ label, value }: { label: string; value: string }) {
    return (
        <BlockStack gap="050">
            <Typography variant="body-sm" tone="text-secondary">
                {label}
            </Typography>
            <Typography variant="body-md" fontWeight="semibold">
                {value}
            </Typography>
        </BlockStack>
    );
}

/**
 * The per-agent drill-in (pv-frontend#163): a header summarising the run —
 * status, trigger, operator, timings and rollup totals — above the per-site
 * breakdown table (disposition + full counts + any per-site error). Presentational
 * only; the container polls while the run is still active.
 */
export function CrawlRunBreakdown({ run, sites }: { run: CrawlRunRow; sites: ReadonlyArray<CrawlSiteRow> }) {
    const status = statusBadge(run.status);
    const partial = partialCoverageWarning(run.sitesPartiallyCrawled);

    return (
        <BlockStack gap="500">
            <Card>
                <BlockStack gap="400">
                    <InlineStack gap="300" blockAlign="center" wrap>
                        <Badge tone={status.tone} dot testID="run-status">
                            {status.label}
                        </Badge>
                        <Badge tone={run.trigger === 'NIGHTLY' ? 'neutral' : 'primary'}>
                            {triggerLabel(run.trigger)}
                        </Badge>
                        <Typography variant="body-sm" tone="text-secondary">
                            {scopeLabel(run.scope)}
                        </Typography>
                        {/* A truncated walk is not an error, so the run still says "Completed" — this badge
                            is the only thing that says the totals below understate the market. */}
                        {partial ? (
                            <Badge tone="warning" dot testID="run-partial-coverage">
                                {partial}
                            </Badge>
                        ) : null}
                    </InlineStack>

                    <InlineStack gap="600" wrap>
                        <Metric label="Operator" value={run.requestedByOperator} />
                        <Metric label="Requested" value={formatTimestamp(run.requestedUtc)} />
                        <Metric label="Started" value={formatTimestamp(run.startedUtc)} />
                        <Metric label="Completed" value={formatTimestamp(run.completedUtc)} />
                    </InlineStack>

                    <InlineStack gap="600" wrap>
                        <Metric label="Sites" value={String(run.sitesCrawled)} />
                        <Metric label="Added" value={String(run.listingsAdded)} />
                        <Metric label="Updated" value={String(run.listingsUpdated)} />
                        <Metric label="Unchanged" value={String(run.listingsUnchanged)} />
                        <Metric label="Missed" value={String(run.listingsMissed)} />
                        <Metric label="Delisted" value={String(run.listingsDelisted)} />
                        <Metric label="Rejects" value={String(run.rejectsQueued)} />
                    </InlineStack>
                </BlockStack>
            </Card>

            <BlockStack gap="300">
                <Typography variant="heading-sm" as="h2" fontWeight="semibold">
                    Per-agent breakdown
                </Typography>
                <DataTable
                    columns={columns}
                    rows={sites}
                    keyExtractor={(site) => site.siteId}
                    testID="crawl-sites"
                    emptyState={
                        <Box padding="0">
                            <Typography tone="text-secondary">
                                No sites recorded yet — the run may still be starting.
                            </Typography>
                        </Box>
                    }
                />
            </BlockStack>
        </BlockStack>
    );
}
